import {
  BaseProductsClient,
  CustomerWishlist,
  Parhato2ProductSubscription,
  Product,
  ProductResults,
  ProductReview,
  ProductReviewPayload,
  ProductsQueryParams,
  ProductSubscription,
  ProductWishlistResponse,
  SearchProductsQueryParams,
  SupaProductResults,
} from "@core/types";
import { getCurrentExpressPointId } from "./utils";
import { supabaseClient } from "../supabase";
import { Tables } from "../types/supabase";
import {
  convertAdditionalFeature,
  converterParhato2Wishlist,
  convertParhato2Product,
  convertParhato2ProductSubscription,
  convertSupaReview,
} from "./converters";
import { SupaProducts, SupaWishlist } from "../types/supabaseFragments";

const DEFAULT_PRODUCTS_PARAMS = {
  status: "publish",
  stock_status: "instock",
  min_price: 0.1,
  per_page: 21,
};

function convertProductsProps(props: ProductsQueryParams) {
  return {
    on_sale: props.saleType,
    page: props.pageNumber,
    per_page: props.pageSize,
    category: props.categoryId,
    orderby: props.sortOrder,
    min_price: props.minPrice,
    max_price: props.maxPrice,
    keyword: props.keyword,
    featured: props.featured,
    additional_feature_filter: props.additionalProductFilter,
    type: props.type,
    productIds: props.productIds?.join(","),
  };
}

class ProductsClient extends BaseProductsClient {
  async getProducts(props: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<SupaProductResults>("/wp-json/wc/v3/products?", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        ...convertProductsProps(props),
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        type: ["variable", "simple"].join(","),
      },
    });

    return {
      additionalFeatures: response.data.additional_features?.map((feature) => convertAdditionalFeature(feature)),
      products: response.data.products?.map((product) => convertParhato2Product(product as any as SupaProducts)),
    };
  }

  async searchProducts(params: SearchProductsQueryParams, zoneId?: string) {
    const response = await this.instance.get<SupaProducts[]>("/wp-json/parhato-api/v1/search-products?", {
      params: {
        q: params.keyword,
        page: params.pageNumber,
        shippingZone: zoneId,
        additionalFeature: params.additionalFeature,
        expressPointId: getCurrentExpressPointId(),
      },
    });

    return response.data.map(convertParhato2Product);
  }

  async getCustomersChoiceProducts(props: ProductsQueryParams) {
    const response = await this.instance.get<SupaProductResults>("wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        orderby: "popularity",
        order: "desc",
        type: ["variable", "simple"].join(","),
      },
    });

    return {
      additionalFeatures: response.data.additional_features?.map((feature) => convertAdditionalFeature(feature)),
      products: response.data.products?.map((product) => convertParhato2Product(product as any as SupaProducts)),
    };
  }

  async getNewestProducts(props: ProductsQueryParams) {
    const response = await this.instance.get<SupaProductResults>("wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        ...convertProductsProps(props),
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        type: ["variable", "simple"].join(","),
      },
    });
    return {
      additionalFeatures: response.data.additional_features?.map((feature) => convertAdditionalFeature(feature)),
      products: response.data.products?.map((product) => convertParhato2Product(product as any as SupaProducts)),
    };
  }

  async getOnSaleProducts(props: ProductsQueryParams) {
    const response = await this.instance.get<SupaProductResults>("wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        ...convertProductsProps(props),
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        on_sale: true,
        type: ["variable", "simple"].join(","),
      },
    });

    return {
      additionalFeatures: response.data.additional_features?.map((feature) => convertAdditionalFeature(feature)),
      products: response.data.products?.map((product) => convertParhato2Product(product as any as SupaProducts)),
    };
  }

  async getWishlist(): Promise<CustomerWishlist | null> {
    const response = await this.instance.get<SupaWishlist>("/wp-json/wc/v3/products/wishlist");
    return response.data ? converterParhato2Wishlist(response.data) : null;
  }

  async getFeaturedProducts(props: ProductsQueryParams) {
    const response = await this.instance.get<SupaProductResults>("wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        ...convertProductsProps(props),
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        featured: true,
        type: ["variable", "simple"].join(","),
      },
    });

    return {
      additionalFeatures: response.data.additional_features?.map((feature) => convertAdditionalFeature(feature)),
      products: response.data.products?.map((product) => convertParhato2Product(product as any as SupaProducts)),
    };
  }

  async getProductById(id: string, zoneId?: string, params?: ProductsQueryParams): Promise<Product> {
    const response = await this.instance.get<SupaProducts>(`/wp-json/wc/v3/products/${id}`, {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        ...(params ? convertProductsProps(params) : {}),
        shippingZone: zoneId,
        expressPointId: getCurrentExpressPointId(),
      },
    });

    return convertParhato2Product(response.data);
  }

  async getIncludeProducts(include: string[], params: { per_page?: string; zoneId?: string }) {
    const response = await this.instance.get<SupaProducts[]>("/wp-json/wc/v3/products", {
      params: {
        include: include.join(","),
        per_page: params.per_page ? params.per_page : include.length + 1,
        shippingZone: params.zoneId,
        expressPointId: getCurrentExpressPointId(),
      },
    });

    return response.data.map(convertParhato2Product);
  }

  async getProductReviews(id: Product["id"]) {
    const { data: productReviews } = await supabaseClient.functions.invoke<Tables<"product_reviews">[]>(
      "get-product-reviews",
      {
        body: { product_id: id },
      },
    );

    return !!productReviews?.length ? productReviews.map(convertSupaReview) : [];
  }

  async addProductReview(productReviewPayload: ProductReviewPayload): Promise<any> {
    const { data: productReviews } = await supabaseClient.functions.invoke<Tables<"product_reviews">>(
      "add-product-review",
      {
        body: { ...productReviewPayload },
      },
    );

    return !!productReviews ? convertSupaReview(productReviews) : [];
  }

  async addProductSubscription(productId: Product["id"]): Promise<Parhato2ProductSubscription | null> {
    const response = await this.instance.post<ProductSubscription>(
      `/wp-json/parhato-api/product-subscriptions/${productId}`,
    );

    return response.data ? convertParhato2ProductSubscription(response.data) : null;
  }

  async getAllProductsSubscriptions(): Promise<Parhato2ProductSubscription[]> {
    const response = await this.instance.get<ProductSubscription[]>("/wp-json/parhato-api/product-subscriptions");
    return response.data.map(convertParhato2ProductSubscription);
  }

  async updateProductsWishlist(productIds: Product["id"][]): Promise<ProductWishlistResponse> {
    if (!this.instance.token) return { success: false, message: "No token provided" };
    const response = await this.instance.post<ProductWishlistResponse>("/wp-json/wc/v3/products/wishlist", {
      product_ids: productIds,
    });

    return response.data ?? { success: false, message: "Failed to update products wishlist" };
  }
}

export default ProductsClient;
